import React, { useEffect, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";

interface AccountDeleteDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	isLoading?: boolean;
	confirmText: string;
	onConfirmTextChange: (text: string) => void;
	errorMessage?: string;
}

export function AccountDeleteDialog({
	isOpen,
	onClose,
	onConfirm,
	isLoading = false,
	confirmText,
	onConfirmTextChange,
	errorMessage,
}: AccountDeleteDialogProps) {
	const [isVisible, setIsVisible] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setIsVisible(true);
			setIsAnimating(true);
			// Prevent body scroll when dialog is open
			document.body.style.overflow = "hidden";
		} else {
			setIsAnimating(false);
			// Re-enable body scroll when dialog closes
			document.body.style.overflow = "unset";
			// Hide after animation completes
			const timer = setTimeout(() => {
				setIsVisible(false);
			}, 200);
			return () => clearTimeout(timer);
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	// Handle ESC key press
	useEffect(() => {
		const handleEscKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && isOpen && !isLoading) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscKey);
		}

		return () => {
			document.removeEventListener("keydown", handleEscKey);
		};
	}, [isOpen, isLoading, onClose]);

	if (!isVisible) return null;

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget && !isLoading) {
			onClose();
		}
	};

	const handleConfirm = () => {
		// Strict validation: must be exactly "LÖSCHEN" (all uppercase)
		const isExactMatch =
			confirmText === "LÖSCHEN" &&
			confirmText.length === 7 &&
			confirmText === confirmText.toUpperCase();

		if (!isLoading && isExactMatch) {
			onConfirm();
		}
	};

	// Strict validation: must be exactly "LÖSCHEN" (all uppercase)
	const isConfirmValid =
		confirmText === "LÖSCHEN" &&
		confirmText.length === 7 &&
		confirmText === confirmText.toUpperCase();

	return (
		<div
			className={`dialog-overlay ${isAnimating ? "dialog-overlay-enter" : "dialog-overlay-exit"}`}
			onClick={handleOverlayClick}
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				background: "rgba(0, 0, 0, 0.5)",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				zIndex: 1000,
				margin: 0,
				padding: 0,
			}}
		>
			<div
				className={`dialog-card ${isAnimating ? "dialog-card-enter" : "dialog-card-exit"}`}
				onClick={(e) => e.stopPropagation()}
				style={{ maxWidth: "400px", width: "90%" }}
			>
				{/* Error Message */}
				{errorMessage && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
						<div className="flex items-start gap-2">
							<AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
							<div>
								<p className="text-sm text-red-700 whitespace-pre-line">
									{errorMessage}
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Warning */}
				{!errorMessage && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
						<div className="flex items-start gap-2">
							<AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
							<div>
								<p
									style={{
										fontWeight: 400,
										fontSize: "14px",
										color: "#7f1d1d",
										margin: 0,
									}}
								>
									Sie sind dabei, Ihren Account und{" "}
									<strong>alle Ihre Daten permanent zu löschen</strong>. Diese
									Aktion kann nicht rückgängig gemacht werden.
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Confirmation Input */}
				<div className="mb-6">
					<p
						style={{
							fontFamily: "Figtree",
							fontWeight: 400,
							fontSize: "14px",
							color: "#6b7280",
							margin: "0 0 8px 0",
						}}
					>
						Um fortzufahren, geben Sie das Wort <strong>"LÖSCHEN"</strong> ein:
					</p>
					<input
						type="text"
						value={confirmText}
						onChange={(e) => onConfirmTextChange(e.target.value)}
						placeholder="LÖSCHEN"
						disabled={isLoading}
						style={{
							width: "100%",
							padding: "8px 12px",
							border: "1px solid #d1d5db",
							borderRadius: "6px",
							fontSize: "14px",
							outline: "none",
						}}
						onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
						onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
					/>
				</div>

				{/* Buttons */}
				<div className="flex gap-3">
					<button
						onClick={onClose}
						disabled={isLoading}
						className={`flex-1 h-12 bg-white border border-[#e5e7eb] rounded-[24px] px-6 transition-colors duration-200 ${
							isLoading
								? "opacity-50 cursor-not-allowed"
								: "hover:bg-[#f9fafb] cursor-pointer"
						}`}
					>
						Abbrechen
					</button>
					<button
						onClick={handleConfirm}
						disabled={!isConfirmValid || isLoading}
						className={`flex-1 h-12 rounded-[24px] px-6 transition-colors duration-200 ${
							!isConfirmValid || isLoading
								? "bg-gray-300 opacity-50 cursor-not-allowed text-gray-500"
								: "bg-red-600 hover:bg-red-700 cursor-pointer text-white"
						}`}
					>
						{isLoading ? "Lösche Account..." : "Account löschen"}
					</button>
				</div>
			</div>
		</div>
	);
}
